import type { AiTrackerReportsBrandOverviewDailyBrandVisibilityGraphData } from "@similarweb/pro-api-client";
import type { IChangeValue } from "@similarweb/ui-components";

import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import { formatAveragePosition } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { IAiTrackerCampaign } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import type {
    IAveragePositionTooltipContent,
    IAveragePositionTooltipInput,
    IBrandOverviewGraphMetric,
    IBrandOverviewResponse,
} from "./types";

export const getRankedSetSize = (configuredCompetitorsCount: number): number => configuredCompetitorsCount + 1;

export const getAveragePositionTooltipContent = ({
    averagePosition,
    mentionPositionRank,
    competitorsCount,
    primaryBrand,
}: IAveragePositionTooltipInput): IAveragePositionTooltipContent => {
    const position = formatAveragePosition(averagePosition);
    const commonParams = {
        brand: primaryBrand,
        position,
    };

    return competitorsCount <= 0
        ? {
              key: "llm.brand_overview.average_position.tooltip",
              params: commonParams,
          }
        : {
              key: "llm.brand_overview.average_position.tooltip.with_competitors",
              params: {
                  ...commonParams,
                  rank: mentionPositionRank ?? NO_DATA_SIGN,
                  total_competitors: getRankedSetSize(competitorsCount),
              },
          };
};

export const GRAPH_PROPS_WITH_PERCENTAGE = {
    chartType: chartTypes.AREA,
    granularity: GranularityNames.DAILY,
    tooltipValueFormatter: (value: number) => percentageSignFilter()(value, 2),
};

export const getConfiguredCompetitorsCount = (campaign?: IAiTrackerCampaign): number =>
    (campaign?.competitors ?? []).filter((competitor) => competitor.brands?.length > 0).length;

export const getPrimaryBrandName = (myBrands?: object | null): string | undefined =>
    myBrands ? Object.keys(myBrands)[0] : undefined;

export const getVisibilityMetric = (data?: Partial<IBrandOverviewResponse>): IBrandOverviewGraphMetric => ({
    share: data?.brandVisibility?.total?.brandVisibilityShare,
    shareChange: data?.brandVisibility?.total?.brandVisibilityShareChange,
    dailyData: data?.brandVisibility?.total?.dailyData,
    brandName: getPrimaryBrandName(data?.brandVisibility?.myBrands),
    mentions: data?.brandVisibility?.total?.brandMentions,
    total: data?.brandVisibility?.total?.totalResponses,
});

export const getMentionShareMetric = (data?: Partial<IBrandOverviewResponse>): IBrandOverviewGraphMetric => ({
    share: data?.brandMentionShare?.total?.brandMentionShare,
    shareChange: data?.brandMentionShare?.total?.brandMentionShareChange,
    dailyData: data?.brandMentionShare?.total?.dailyData,
    brandName: getPrimaryBrandName(data?.brandMentionShare?.myBrands),
    mentions: data?.brandMentionShare?.total?.brandMentions,
    total: data?.brandMentionShare?.total?.totalAllBrandMentions,
});

export const getGraphDataFromArray = (
    dailyDataArray: AiTrackerReportsBrandOverviewDailyBrandVisibilityGraphData[] | undefined,
    name: string,
) => {
    if (!dailyDataArray?.length || !name) {
        return null;
    }

    return [
        {
            name,
            id: name,
            visible: true,
            data: dailyDataArray.map((item) => [new Date(item.date ?? "").getTime(), item.value]),
        },
    ];
};

export const getPopChangeValueProps = (change: number | null | undefined): IChangeValue | undefined => {
    if (change == null) {
        return undefined;
    }
    return {
        value: Math.abs(change).toFixed(2),
        isDecrease: change < 0,
        descriptionText: translate("llm.brand_overview.pop"),
    };
};
